import {
	AnimationsBehavior,
	BackgroundMedia,
	BackgroundMediaFillLayerCSSVars,
	ColorUnderlayLayerCSSVars,
	MediaCSSVars,
	VideoFillLayerCSSVars,
} from '@wix/thunderbolt-becky-types'
import { color as _colorApi } from '@wix/thunderbolt-commons'
import { CompInfo } from '@wix/thunderbolt-components-native'
import _ from 'lodash'
import { getMediaTransformsCSSVars, shouldRenderBackgroundMediaLayer } from '../BackgroundMedia/BackgroundMedia.utils'
import { getBackgroundOverlayFillLayerCSSVars, parseColorToRGBA } from '../BackgroundOverlay/BackgroundOverlay.utils'
import { getImageFillLayerCSSVars } from '../Image/Image.utils'
import { hasBackgroundScrubEffect, isMediaFullScreenByEffect } from '../Media/Media.utils'
import { getMediaPaddingLayerCSSVars } from '../MediaPadding/MediaPadding.utils'
import { getVideoFillLayerCSSVars } from '../Video/Video.utils'

const { generateGradient } = _colorApi

export const getBackgroundMediaFillLayerCSSVars = ({
	animations,
	viewMode,
}: Pick<CompInfo, 'experiments' | 'viewMode'> & {
	animations: Array<AnimationsBehavior>
}): BackgroundMediaFillLayerCSSVars | undefined => {
	const shouldRenderBackgroundMedia = shouldRenderBackgroundMediaLayer({
		animations,
		viewMode,
	})
	if (shouldRenderBackgroundMedia) {
		const hasBgScrubEffect = hasBackgroundScrubEffect({ animations, viewMode })
		const isFullScreenByEffect = isMediaFullScreenByEffect({
			animations,
			viewMode,
		})

		const backgroundMediaCSSVars = {
			'--fill-layer-background-media-position': isFullScreenByEffect ? 'fixed' : 'absolute',
			'--fill-layer-background-media-pointer-events': hasBgScrubEffect ? 'none' : 'auto',
		} as const

		return backgroundMediaCSSVars
	}
}

const getColorUnderlayLayerCSSVars = ({
	backgroundMedia,
	siteColors,
}: {
	backgroundMedia: BackgroundMedia
	siteColors: Array<string>
}): ColorUnderlayLayerCSSVars => {
	const { opacity, fill } = backgroundMedia.colorLayers?.[0] ?? {}
	const colorLayerOpacity = opacity ?? 1

	const { type: colorType, color, gradients } = fill ?? {}
	const isSolidColor = colorType === 'SolidColor'
	const isMesh = colorType === 'GradientMesh'
	// const isSingleGradient = and(colorType, or(isSolidColor, isMesh).not())
	const isSingleGradient = colorType && !isSolidColor && !isMesh
	const colorLayer = isSolidColor ? parseColorToRGBA(color, colorLayerOpacity) : undefined
	const gradientLayer = isMesh
		? generateGradient(gradients, colorLayerOpacity, siteColors)
		: isSingleGradient
		? generateGradient(fill, colorLayerOpacity, siteColors)
		: null

	const legacyColor = parseColorToRGBA(backgroundMedia.color || 'none', backgroundMedia.colorOpacity)
	if (gradientLayer) {
		return {
			'--bg-gradient': gradientLayer,
			'--bg-overlay-color': 'transparent',
		}
	}

	return {
		'--bg-overlay-color': colorLayer || legacyColor,
		'--bg-gradient': 'none',
	}
}

export const getFillLayersColorsVars = ({
	siteColors,
	backgroundMedia,
}: Pick<CompInfo, 'siteColors'> & { backgroundMedia: BackgroundMedia }) =>
	getColorUnderlayLayerCSSVars({ siteColors, backgroundMedia })

export const getFillLayersMediaVars = ({
	animations,
	compProps,
	compDesign,
	compId,
	backgroundMedia,
	isNonAdaptiveMobile,
	experiments,
	isMobileView,
	viewMode,
}: Pick<CompInfo, 'compId' | 'compProps' | 'compDesign' | 'experiments' | 'isMobileView' | 'viewMode'> & {
	animations: Array<AnimationsBehavior>
	isNonAdaptiveMobile: boolean
	backgroundMedia: BackgroundMedia
}): Partial<MediaCSSVars & VideoFillLayerCSSVars> => {
	const imageLayerCSSVars = getImageFillLayerCSSVars(backgroundMedia, compId)
	const videoLayerCSSVars = getVideoFillLayerCSSVars(backgroundMedia)
	const backgroundMediaCSSVars = getBackgroundMediaFillLayerCSSVars({
		animations,
		experiments,
		viewMode,
	})

	const backgroundOverlayLayerCSSVars = getBackgroundOverlayFillLayerCSSVars({
		animations,
		experiments,
		isMobileView,
		isNonAdaptiveMobile,
		viewMode,
		backgroundMedia,
	})

	const mediaPaddingLayerCSSVars = getMediaPaddingLayerCSSVars({ compProps, isMobileView })
	const transformsData = compDesign ? getMediaTransformsCSSVars({ compDesign, backgroundMedia }) : {}

	return _.pickBy(
		{
			...imageLayerCSSVars,
			...videoLayerCSSVars,
			...backgroundMediaCSSVars,
			...backgroundOverlayLayerCSSVars,
			...mediaPaddingLayerCSSVars,
			...transformsData,
		},
		(v) => v || typeof v === 'number'
	)
}

export const getFillLayersCSSVars = ({
	animations,
	compProps,
	compDesign,
	siteColors,
	compId,
	backgroundMedia,
	isNonAdaptiveMobile,
	experiments,
	isMobileView,
	viewMode,
}: Pick<
	CompInfo,
	'compId' | 'compProps' | 'compDesign' | 'siteColors' | 'experiments' | 'isMobileView' | 'viewMode'
> & {
	animations: Array<AnimationsBehavior>
	isNonAdaptiveMobile: boolean
	backgroundMedia: BackgroundMedia
}): Partial<MediaCSSVars & VideoFillLayerCSSVars> => ({
	...getFillLayersMediaVars({
		animations,
		compProps,
		compDesign,
		compId,
		backgroundMedia,
		isNonAdaptiveMobile,
		experiments,
		isMobileView,
		viewMode,
	}),
	...getFillLayersColorsVars({ siteColors, backgroundMedia }),
})
